import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import { coerceBhConnectionCount } from './bhConnectionCoerce.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE } from './whoWereReachingSnapshotDetect.util';

/** Peer-reviewed per-member savings assumptions shown in Studio copy. */
export const CLOSING_VALUE_BH_PER_MEMBER_SAVINGS = 2000;

/** Peer-reviewed per-member savings assumptions shown in Studio copy. */
export const CLOSING_VALUE_PCP_PER_MEMBER_SAVINGS = 4000;

/** One cost-reduction equation row (BH or PCP). */
export interface ClosingValueCostReductionRow {
  leftLabel: string;
  leftValue: number;
  rightLabel: string;
  rightValue: number;
  resultLabel: string;
  resultValue: number;
  theme: 'bh' | 'pcp';
}

type BhConnectionRow = {
  ct_no_pre_eng_bh_visit?: unknown;
  ct_post_eng_bh_visit_claims_reema?: unknown;
  cost_reduction_pred_bh_claims_reema?: unknown;
  ct_no_pre_eng_pcp_visit?: unknown;
  ct_post_eng_pcp_visit_claims_reema?: unknown;
  cost_reduction_pred_pcp_claims_reema?: unknown;
};

/**
 * Formats a savings total for the cost-reduction tile (e.g. `$274K`).
 * @param amount - Dollar amount.
 * @returns Compact currency label.
 */
export const formatClosingValueSavings = (amount: number): string => {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${Math.round(amount / 1_000)}K`;
  }
  return `$${amount.toLocaleString('en-US')}`;
};

const resolveBhSavings = (
  row: BhConnectionRow,
  membersWithVisit: number,
): number => {
  const warehouse = coerceBhConnectionCount(row.cost_reduction_pred_bh_claims_reema);
  if (warehouse > 0) {
    return warehouse;
  }
  return membersWithVisit * CLOSING_VALUE_BH_PER_MEMBER_SAVINGS;
};

const resolvePcpSavings = (
  row: BhConnectionRow,
  membersWithVisit: number,
): number => {
  const warehouse = coerceBhConnectionCount(row.cost_reduction_pred_pcp_claims_reema);
  if (warehouse > 0) {
    return warehouse;
  }
  return membersWithVisit * CLOSING_VALUE_PCP_PER_MEMBER_SAVINGS;
};

/**
 * Returns true when warehouse BH/PCP connection metrics exist for the bottom line.
 * @param row - Current-period BH connection aggregate row.
 * @returns Whether a warehouse snapshot should be built.
 */
export const closingValueHasWarehouseData = (row: BhConnectionRow): boolean =>
  coerceBhConnectionCount(row.ct_no_pre_eng_bh_visit) > 0 ||
  coerceBhConnectionCount(row.ct_post_eng_bh_visit_claims_reema) > 0 ||
  coerceBhConnectionCount(row.ct_no_pre_eng_pcp_visit) > 0 ||
  coerceBhConnectionCount(row.ct_post_eng_pcp_visit_claims_reema) > 0;

/**
 * Builds the closing-value snapshot from BH connection warehouse rows.
 * @param currentRow - Aggregate row for the report end month.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Snapshot object or null when warehouse data is empty.
 */
export const buildClosingValueSnapshotFromRow = (
  currentRow: BhConnectionRow,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> | null => {
  if (!closingValueHasWarehouseData(currentRow)) {
    return null;
  }

  const mockBase = mockSectionData('closing-value', customer, reportingPeriod);
  const bhNoVisit = coerceBhConnectionCount(currentRow.ct_no_pre_eng_bh_visit);
  const bhWithVisit = coerceBhConnectionCount(
    currentRow.ct_post_eng_bh_visit_claims_reema,
  );
  const pcpNoVisit = coerceBhConnectionCount(currentRow.ct_no_pre_eng_pcp_visit);
  const pcpWithVisit = coerceBhConnectionCount(
    currentRow.ct_post_eng_pcp_visit_claims_reema,
  );
  const bhSavings = resolveBhSavings(currentRow, bhWithVisit);
  const pcpSavings = resolvePcpSavings(currentRow, pcpWithVisit);
  const total = bhSavings + pcpSavings;

  const fieldSources: ReportSectionFieldSources = {
    costReductionRows: 'warehouse',
    total: 'warehouse',
    summary: 'mock',
  };

  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    summary: `Removing barriers and engaging members in care drives total cost of care reduction. Per peer-reviewed research, the average annual savings for initiation into BH outpatient care is $2,000 per member, and the average annual savings for a first PCP visit is $4,000 per member. Applied to Reema's ${customer.name} engaged population, the projected savings are below.`,
    costReductionRows: [
      {
        leftLabel: 'Members w/ no BH visit in last 12 mo',
        leftValue: bhNoVisit,
        rightLabel: 'Members w/ BH visit since Reema',
        rightValue: bhWithVisit,
        resultLabel: 'Cost reduction prediction',
        resultValue: bhSavings,
        theme: 'bh',
      },
      {
        leftLabel: 'Members w/ no PCP visit in last 12 mo',
        leftValue: pcpNoVisit,
        rightLabel: 'Members w/ PCP visit since Reema',
        rightValue: pcpWithVisit,
        resultLabel: 'Cost reduction prediction',
        resultValue: pcpSavings,
        theme: 'pcp',
      },
    ] satisfies ClosingValueCostReductionRow[],
    costReductionMath: [
      { label: 'BH connections', value: bhSavings },
      { label: 'PCP connections', value: pcpSavings },
    ],
    total,
    combinedLabel: 'Combined projected savings from connection to care alone',
    fieldSources,
  };
};
